import React from "react";
// @Mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// formik
import { useFormik } from "formik";
// yup
import * as Yup from "yup";
// recoil
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
// atoms
import { freezePopUpAtom } from "src/recoil/atoms/freezePopUpAtom";
import userAtom from "src/recoil/atoms/userAtom";
import alertAtom from "src/recoil/atoms/alertAtom";
// __apis__
import { freezeHandler } from "src/__apis__/clientInfo";

// ---------------------------------------------------------------------------------------------

function FreezePopUp() {
  const user = useRecoilValue(userAtom);

  const triggerAlert = useSetRecoilState(alertAtom);

  const calcMaxFreezeDuration = () => {
    const subDuration = Number(user?.freeze);
    return subDuration;
  };

  const formik = useFormik({
    initialValues: {
      freezeDuration: 1,
    },
    validationSchema: Yup.object().shape({
      freezeDuration: Yup.number()
        .min(0, "Freeze duration cannot be less than 1")
        .max(
          calcMaxFreezeDuration(),
          "Freeze duration cannot be more than your plan duration"
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      await freezeHandler(values.freezeDuration)
        .then((response) => {
          triggerAlert({
            triggered: true,
            type: "success",
            message: "Freeze activated successfully.",
          });
          closeHandler();
          resetForm();
        })
        .catch((error) => {
          console.log("Something wrong happened activating freeze", error);
          triggerAlert({
            triggered: true,
            type: "error",
            message: "Couldn't activate freeze at the moment.",
          });
        });
    },
  });

  const [open, setOpen] = useRecoilState(freezePopUpAtom);

  const closeHandler = () => {
    setOpen(false);
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    errors,
    dirty,
    touched,
    getFieldProps,
  } = formik;

  return (
    <Dialog open={open} onClose={closeHandler}>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">Freeze</Typography>
          <Typography variant="subtitle1" color="primary">{`${
            user?.freeze
          } / ${Math.trunc(
            Number(user?.plan_duration) * 30 * 0.25
          )}`}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are You Sure You Want To Activate Your Freeze ?!
        </DialogContentText>
        <Box sx={{ pt: 3 }}>
          {user?.freeze === 0 ? (
            <Typography align="center" color="error" variant="subtitle1">
              You Ran Out Of Freeze
            </Typography>
          ) : (
            <TextField
              label="Freeze Duration"
              type="number"
              value={values.freezeDuration}
              onChange={(event) =>
                setFieldValue("freezeDuration", event.target.value)
              }
              error={touched.freezeDuration && errors.freezeDuration}
              helperText={touched.freezeDuration && errors.freezeDuration}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">DAYS</InputAdornment>
                ),
              }}
              {...getFieldProps("freezeDuration")}
              fullWidth
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={closeHandler} color="error">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          disabled={!dirty}
          loading={isSubmitting}
          variant="contained"
        >
          Activate Freeze
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default FreezePopUp;
